import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Loader2, Plus, Send, CheckCircle2, X, Link2, AlertTriangle, CalendarDays, Trash2,
  CalendarOff, Info, Hourglass,
} from 'lucide-react';

/**
 * BookMeProposalsPanel — OL BookMe multi-date game proposals, per sport.
 *
 * Offer a peer school several candidate dates for one team; they accept one,
 * decline, or counter-propose through a tokenized link (no account). Confirming
 * creates a **tentative** game in the OL schedule — it is NOT published. The
 * Scheduler releases the confirmed schedule to the website from the grid.
 *
 * API `/api/booking-proposals`. Doc: `OL_BOOKME.md`.
 */
const STATUS_STYLE = {
  draft: 'bg-slate-500/70',
  sent: 'bg-blue-600',
  answered: 'bg-amber-600',
  confirmed: 'bg-emerald-600',
  cancelled: 'bg-slate-600',
  declined: 'bg-red-600',
  expired: 'bg-slate-700',
};

export default function BookMeProposalsPanel({ orgId, sport, sportLabel = '', isDark = true }) {
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const API = `${BASE}/api/booking-proposals`;
  // Self-sufficient: resolve this sport's current season and its team columns
  // from the schedule API, so the hub only has to pass orgId + sport.
  const [season, setSeason] = useState('');
  const [teamColumns, setTeamColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const blank = {
    team_slug: '', team_label: '', opponent_name: '', home_away: 'home',
    location: '', note: '', dates: ['', '', ''], peer_email: '', peer_name: '',
  };
  const [form, setForm] = useState(blank);

  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const dim = isDark ? 'text-slate-500' : 'text-slate-400';
  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const inputCls = isDark ? 'bg-slate-800 border-slate-600 text-white' : '';
  const selCls = `h-8 text-xs rounded-md px-2 border w-full ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`;

  const load = useCallback(async () => {
    if (!orgId || !sport) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${orgId}`, {
        params: { sport, ...(season ? { season } : {}) }, withCredentials: true,
      });
      setRows(data?.proposals || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load proposals.');
    } finally { setLoading(false); }
  }, [orgId, sport, season, API]);

  useEffect(() => { load(); }, [load]);

  // Current season for this sport, then the season's team columns (the same
  // canonical level+variant set the Scheduler grid shows — LEVEL_LABELS.md).
  useEffect(() => {
    if (!orgId || !sport) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BASE}/api/game-schedule/${orgId}/sports`,
          { withCredentials: true });
        const sp = (data?.sports || []).find(x => x.slug === sport);
        const y = Number(sp?.current_season_year) || 0;
        if (!y || !alive) return;
        const label = `${y}-${y + 1}`;
        setSeason(label);
        const grid = await axios.get(`${BASE}/api/game-schedule/${orgId}`,
          { params: { sport, season: label }, withCredentials: true }).catch(() => null);
        if (alive && grid?.data) setTeamColumns(grid.data.team_columns || []);
      } catch { /* the panel still works; the team picker is just empty */ }
    })();
    return () => { alive = false; };
  }, [orgId, sport, BASE]);

  const create = async () => {
    const dates = form.dates.map(d => d.trim()).filter(Boolean);
    if (!form.team_slug || !form.opponent_name.trim() || dates.length === 0) {
      toast.error('Team, opponent and at least one date are required.');
      return;
    }
    setBusy('create');
    try {
      const { data } = await axios.post(`${API}/${orgId}`, {
        sport, season, team_slug: form.team_slug,
        team_label: form.team_label || undefined,
        opponent_name: form.opponent_name.trim(),
        home_away: form.home_away,
        location: form.location || '',
        note: form.note || '',
        dates,
        peer_contact: (form.peer_email || form.peer_name)
          ? { email: form.peer_email.trim(), name: form.peer_name.trim() } : undefined,
      }, { withCredentials: true });
      setOpen(false); setForm(blank); await load();
      toast.success(`Proposal created with ${dates.length} date(s).`);
      const findings = data?.conflicts || {};
      const blacked = Object.entries(findings)
        .filter(([, fs]) => fs.some(f => f.severity === 'blackout')).map(([d]) => d);
      const warned = Object.entries(findings)
        .filter(([, fs]) => fs.some(f => f.severity === 'warning')).map(([d]) => d);
      if (blacked.length) toast.warning(`Blackout date(s): ${blacked.join(', ')} — you can still offer them.`);
      else if (warned.length) toast.warning(`Possible clash on ${warned.join(', ')} — open the proposal for detail.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the proposal.');
    } finally { setBusy(''); }
  };

  const act = async (p, path, payload, okMsg) => {
    setBusy(p.id);
    try {
      const { data } = await axios.post(`${API}/${orgId}/${p.id}/${path}`, payload || {},
        { withCredentials: true });
      await load();
      if (detail?.id === p.id) await openDetail(p);
      toast.success(okMsg(data));
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work.');
    } finally { setBusy(''); }
  };

  const openDetail = async (p) => {
    try {
      const { data } = await axios.get(`${API}/${orgId}/${p.id}`, { withCredentials: true });
      setDetail({ ...data.proposal, _conflicts: data.conflicts || {}, _link: data.peer_link });
    } catch { toast.error('Could not open that proposal.'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete this proposal to ${p.opponent_name}? A confirmed game is not removed.`)) return;
    try {
      await axios.delete(`${API}/${orgId}/${p.id}`, { withCredentials: true });
      setDetail(null); await load(); toast.success('Proposal deleted.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  const copyLink = (link) => {
    try { navigator.clipboard.writeText(link); toast.success('Peer link copied.'); }
    catch { toast.error('Could not copy — select the link and copy manually.'); }
  };

  return (
    <div className="space-y-3" data-testid="bookme-proposals-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => setOpen(true)} disabled={!season}
          className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
          title={season ? 'Offer a peer school several candidate dates'
                        : 'Pick a season on the Schedule tab first'}
          data-testid="bookme-new-proposal-btn">
          <Plus className="h-3 w-3 mr-1" />Propose dates
        </Button>
        {loading && <Loader2 className={`h-3 w-3 animate-spin ${dim}`} />}
        <span className={`text-[11px] ml-auto ${dim}`}>
          {sportLabel || sport}{season ? ` · ${season}` : ''} — confirming creates a
          <strong> tentative</strong> game; release to the website from the Schedule tab.
        </span>
      </div>

      {!loading && rows.length === 0 && (
        <Card className={cardCls}>
          <CardContent className="p-8 text-center">
            <p className={`text-sm ${muted}`}>No game proposals for this sport yet.</p>
            <p className={`text-xs mt-1 ${dim}`}>
              “Propose dates” offers another school a few options for one team. They answer
              through a link — no account needed.
            </p>
          </CardContent>
        </Card>
      )}

      {rows.map(p => {
        const open_ = (p.dates || []).filter(d => d.status === 'open').length;
        const accepted = (p.dates || []).find(d => d.status === 'accepted');
        return (
          <Card key={p.id} className={cardCls} data-testid={`bookme-row-${p.id}`}>
            <CardContent className="p-3 flex items-start gap-2 flex-wrap">
              <button className="text-left" onClick={() => openDetail(p)} data-testid={`bookme-open-${p.id}`}>
                <span className={`text-sm font-semibold ${text}`}>
                  {p.team_label} {p.home_away === 'home' ? 'vs' : '@'} {p.opponent_name}
                </span>
                <span className={`block text-[11px] ${dim}`}>
                  {(p.dates || []).length} date(s){open_ ? ` · ${open_} open` : ''}
                  {accepted ? ` · accepted ${accepted.date}` : ''}
                  {p.ol_game_id ? ' · tentative game created' : ''}
                </span>
              </button>
              <div className="flex items-center gap-2 ml-auto">
                <Badge className={`${STATUS_STYLE[p.status] || 'bg-slate-500'} text-[10px]`}>{p.status}</Badge>
                {p.expiring_soon && (
                  <span className="flex items-center gap-1 text-amber-500 text-[10px]"
                    title={`No answer yet — this proposal lapses on ${(p.expires_at || '').slice(0, 10)}`}
                    data-testid={`bookme-expiring-${p.id}`}>
                    <Hourglass className="h-3 w-3" />expires {(p.expires_at || '').slice(5, 10)}
                  </span>
                )}
                {(p.status === 'draft' || p.status === 'expired') && (
                  <Button size="sm" variant="outline" disabled={busy === p.id}
                    onClick={() => act(p, 'send', null, d => d.emailed ? 'Sent to the peer school.' : 'Marked sent — copy the link to share it.')}
                    title={p.status === 'expired' ? 'Re-send: reopens the proposal with a fresh window' : undefined}
                    className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                    data-testid={`bookme-send-${p.id}`}>
                    <Send className="h-3 w-3 mr-1" />{p.status === 'expired' ? 'Re-send' : 'Send'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openDetail(p)}
                  className={`h-7 text-xs ${muted}`}>Open</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* ── New proposal ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>Propose dates</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1" data-testid="bookme-new-dialog">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={`text-xs ${muted}`}>Team</Label>
                <select className={selCls} value={form.team_slug}
                  onChange={e => {
                    const col = teamColumns.find(c => c.slug === e.target.value);
                    setForm(f => ({ ...f, team_slug: e.target.value, team_label: col?.label || '' }));
                  }}
                  data-testid="bookme-team">
                  <option value="">Choose a team…</option>
                  {teamColumns.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Home or away</Label>
                <select className={selCls} value={form.home_away}
                  onChange={e => setForm(f => ({ ...f, home_away: e.target.value }))}
                  data-testid="bookme-home-away">
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>
              </div>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Opposing school / team</Label>
              <Input value={form.opponent_name} className={inputCls}
                onChange={e => setForm(f => ({ ...f, opponent_name: e.target.value }))}
                placeholder="e.g. Cary Academy Varsity Chargers" data-testid="bookme-opponent" />
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Candidate dates</Label>
              <div className="grid grid-cols-3 gap-2">
                {form.dates.map((d, i) => (
                  <Input key={i} type="date" value={d} className={inputCls}
                    onChange={e => setForm(f => {
                      const nd = [...f.dates]; nd[i] = e.target.value; return { ...f, dates: nd };
                    })}
                    data-testid={`bookme-date-${i}`} />
                ))}
              </div>
              <button className={`text-[11px] mt-1 ${muted} underline`}
                onClick={() => setForm(f => ({ ...f, dates: [...f.dates, ''] }))}
                data-testid="bookme-add-date">+ another date</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={`text-xs ${muted}`}>Their contact email (optional)</Label>
                <Input value={form.peer_email} className={inputCls} type="email"
                  onChange={e => setForm(f => ({ ...f, peer_email: e.target.value }))}
                  placeholder="scheduler@school.org" data-testid="bookme-peer-email" />
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Venue / location (optional)</Label>
                <Input value={form.location} className={inputCls}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  data-testid="bookme-location" />
              </div>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Note to the other school (optional)</Label>
              <Input value={form.note} className={inputCls}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Any of these work for us — JV at 4, Varsity at 6." data-testid="bookme-note" />
            </div>
            <p className={`text-[11px] ${dim}`}>
              Offering dates reserves nothing — a date stays free until you confirm it.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} className={muted}>Cancel</Button>
              <Button onClick={create} disabled={busy === 'create'}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bookme-create">
                {busy === 'create' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Create proposal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Detail / confirm ── */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className={`max-w-xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={text}>
              {detail?.team_label} {detail?.home_away === 'home' ? 'vs' : '@'} {detail?.opponent_name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 py-1" data-testid="bookme-detail">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${STATUS_STYLE[detail.status]} text-[10px]`}>{detail.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => copyLink(detail._link)}
                  className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                  data-testid="bookme-copy-link">
                  <Link2 className="h-3 w-3 mr-1" />Copy peer link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(detail)}
                  className={`h-7 text-xs ml-auto ${muted}`} data-testid="bookme-delete">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              <div className="space-y-1">
                {(detail.dates || []).map(d => {
                  const findings = (detail._conflicts || {})[d.date] || [];
                  const isBlackout = findings.some(f => f.severity === 'blackout');
                  const warnings = findings.filter(f => f.severity === 'warning');
                  const infos = findings.filter(f => f.severity === 'info');
                  return (
                    <div key={d.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs ${
                        d.status === 'accepted'
                          ? (isDark ? 'border-emerald-700 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50')
                          : d.status === 'rejected'
                            ? `${isDark ? 'border-slate-700' : 'border-slate-200'} opacity-50`
                            : (isDark ? 'border-slate-700' : 'border-slate-200')}`}
                      data-testid={`bookme-date-row-${d.id}`}>
                      <CalendarDays className={`h-3 w-3 ${dim}`} />
                      <span className={text}>{d.date}</span>
                      {d.time && <span className={muted}>{d.time}</span>}
                      {d.proposed_by === 'peer' && (
                        <Badge variant="outline" className="text-[9px]">their suggestion</Badge>
                      )}
                      {d.status === 'accepted' && (
                        <Badge className="bg-emerald-600 text-[9px]">accepted</Badge>
                      )}
                      {isBlackout && (
                        <span className="flex items-center gap-1 text-red-400 text-[10px]"
                          title={findings.filter(f => f.severity === 'blackout').map(f => f.detail).join(' · ')}>
                          <CalendarOff className="h-3 w-3" />
                          {findings.find(f => f.severity === 'blackout').detail}
                        </span>
                      )}
                      {warnings.length > 0 && (
                        <span className="flex items-center gap-1 text-amber-500 text-[10px]"
                          title={warnings.map(f => f.detail).join(' · ')}>
                          <AlertTriangle className="h-3 w-3" />{warnings[0].detail}
                          {warnings.length > 1 ? ` +${warnings.length - 1}` : ''}
                        </span>
                      )}
                      {infos.length > 0 && warnings.length === 0 && !isBlackout && (
                        <span className={`flex items-center gap-1 text-[10px] ${dim}`}
                          title={infos.map(f => f.detail).join(' · ')}>
                          <Info className="h-3 w-3" />{infos[0].detail}
                        </span>
                      )}
                      {detail.status !== 'confirmed' && d.status !== 'rejected' && (
                        <Button size="sm"
                          onClick={() => {
                            // A blackout is the one finding that stops a confirm. Ask once,
                            // then send the explicit override — never silently.
                            if (isBlackout && !window.confirm(
                                `${d.date} is a blackout date (${findings.find(f => f.severity === 'blackout').detail}).

`
                                + 'Schedule a game on it anyway?')) return;
                            act(detail, 'confirm',
                              { date_id: d.id, ...(isBlackout ? { allow_blackout: true } : {}) },
                              () => 'Confirmed — a tentative game was added to the schedule.');
                          }}
                          disabled={busy === detail.id}
                          className={`h-6 text-[11px] ml-auto text-white ${isBlackout
                            ? 'bg-red-700 hover:bg-red-600' : 'bg-emerald-700 hover:bg-emerald-600'}`}
                          data-testid={`bookme-confirm-${d.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {isBlackout ? 'Confirm anyway' : 'Confirm this date'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              {detail.status === 'confirmed' && (
                <div className={`text-[11px] rounded-md border px-3 py-2 ${isDark
                    ? 'border-emerald-700/50 bg-emerald-900/15 text-emerald-300'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`}
                  data-testid="bookme-confirmed-note">
                  A <strong>tentative</strong> game is in the OL schedule. It is not on the website —
                  release it from the Schedule tab once the season is confirmed.
                </div>
              )}

              {(detail.thread || []).length > 0 && (
                <div className={`text-[11px] border-t pt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  {(detail.thread || []).map((t, i) => (
                    <div key={i} className={muted}>
                      <span className={dim}>{(t.at || '').slice(0, 16).replace('T', ' ')}</span>{' '}
                      <strong className={text}>{t.by === 'peer' ? (t.by_name || 'They') : 'We'}</strong>{' '}
                      {t.action}{t.date ? ` ${t.date}` : ''}
                      {t.counter_dates ? ` (${t.counter_dates.join(', ')})` : ''}
                      {t.note ? ` — ${t.note}` : ''}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2">
                {detail.status !== 'confirmed' && detail.status !== 'cancelled' && (
                  <Button variant="ghost" className={muted} disabled={busy === detail.id}
                    onClick={() => act(detail, 'cancel', null, () => 'Proposal withdrawn.')}
                    data-testid="bookme-cancel">
                    <X className="h-4 w-4 mr-1" />Withdraw
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setDetail(null)} className={muted}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
